import { useState, useEffect } from 'react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { Save, CheckCircle, AlertCircle, Loader2, X, Plus, LogOut, Clock, ChevronDown, BookOpen, Lock, Pencil } from 'lucide-react'
import { useApi, useAsanaApi } from '../hooks/useApi'
import { useAuth } from '../hooks/useAuth'
import { useData } from '../hooks/useData'
import type { Settings as SettingsType, Project } from '../types'
import { inputClass } from '../components/Field'
import LoadingSpinner from '../components/LoadingSpinner'
import Modal from '../components/Modal'
import SettingsTabs from '../components/SettingsTabs'

const DOCS = [
  {
    title: 'KPI Definitions',
    content: [
      { term: 'On Time', def: `% of completed projects where go-live was within {{onTimeDays}} days of the planned due date.\nFormula: (projects on time ÷ total projects) × 100\nExample: 6 out of 10 projects on time → 6 ÷ 10 × 100 = 60%\nRule: go-live date − due date ≤ {{onTimeDays}} days` },
      { term: 'At Cost', def: `% of completed projects where actual spend did not exceed forecast by more than {{atCostPct}}%. Projects with no actual cost are excluded.\nFormula: (projects at cost ÷ total projects) × 100\nExample: 7 out of 10 projects at cost → 7 ÷ 10 × 100 = 70%\nRule: actual cost ≤ forecast × (1 + {{atCostPct}}%)` },
      { term: 'In Full', def: `% of completed projects marked Delivered in Full = Yes.\nFormula: (projects delivered in full ÷ total projects) × 100\nExample: 8 out of 10 projects delivered in full → 8 ÷ 10 × 100 = 80%` },
      { term: 'Satisfaction', def: `Average score across both survey questions (solution quality 1-5, team rating 1-5) for all responses.\nFormula: sum of all scores ÷ (number of responses × 2)\nExample: 3 responses with scores [4,5], [3,4], [5,5] → (4+5+3+4+5+5) ÷ (3×2) = 26 ÷ 6 = 4.3` },
      { term: 'Adoption', def: `Total actual users across all contributing projects as a % of total expected users. Sum-then-divide, not averaged. Capped at 100%.\nFormula: (sum of actual users ÷ sum of expected users) × 100\nExample: 130 actual users, 200 expected → 130 ÷ 200 × 100 = 65%\n\nEach project picks how adoption is measured:\n• Users (count) - enter Expected + Actual Users; Adoption = actual ÷ expected\n• Direct % - enter a self-reported rate from a vendor dashboard; Expected Users is still needed as the roll-up weight, and Actual is back-calculated (rate × expected) so every project shares one formula\n• Not applicable - no meaningful user base (back-office tools, data pipelines); excluded from the KPI entirely` },
      { term: 'Hrs Saved', def: `Total hours saved across all completed projects, sourced from each project's monthly ledger.\nThe ledger is auto-seeded when a project completes (Expected Value ÷ 12, prorated from go-live month). Owners can then adjust individual months.\nFormula: sum of (base + adjustments) across all ledger entries` },
      { term: 'Tolerances', def: `On Time grace period: {{onTimeDays}} days - projects going live up to {{onTimeDays}} days after the due date still count as on time.\nAt Cost allowance: {{atCostPct}}% - projects spending up to {{atCostPct}}% over forecast still count as at cost.` },
    ],
  },
  {
    title: 'Project Lifecycle',
    content: [
      { term: 'Forecast', def: `Default state for all new projects. Counted in the Pipeline tiles on Dashboard and Reports, but NOT in the realised KPI tiles.` },
      { term: 'Complete', def: `Set manually once an actual go-live date is entered. The system rejects status='Complete' without an actualGoLive date. On completion, the project's monthly ledger is auto-seeded from its Expected Value. Only complete projects feed the realised KPIs and the headline 'hrs saved' figure on Reports.` },
      { term: 'Cancelled', def: `Project was abandoned. Excluded from both pipeline and realised KPIs.` },
      { term: 'Survey send', def: `The survey button is unlocked once configuration is complete. IT clicks "Send Survey" manually - there is no automatic send. Stakeholders receive a personalised link via email (Power Automate).` },
    ],
  },
  {
    title: 'Risks & Forecast KPIs',
    content: [
      { term: 'Risk assessment', def: `Every risk is assessed on the Danone risk matrix: Impact and Likelihood, each rated Very Low (1), Low (2), High (3) or Very High (4).\nFormula: risk rating = impact score × likelihood score\nExample: Impact Very High (4) × Likelihood High (3) → rating 12 → Red\nRule: Red ≥ 9, Amber 4-8, Green ≤ 3` },
      { term: 'Impact', def: `How big the hit is if the risk materialises, guided by the Danone register thresholds:\n• Very Low - <€10K or <5% budget / <2 weeks timeline\n• Low - €10-25K or 5-10% / 2-5 weeks\n• High - €25-50K or 10-15% / 5-10 weeks\n• Very High - >€50K or >15% / >10 weeks` },
      { term: 'Likelihood', def: `The chance that the cause of the event leads to the effect:\n• Very Low - <2%\n• Low - 2-5%\n• High - 5-10%\n• Very High - >10%` },
      { term: 'Mitigation', def: `Mandatory for every risk - the "ability to handle the risk / risk response" from the Danone register. As a minimum: "working on a mitigation plan" with a deadline date. Risks cannot be saved without one.` },
      { term: 'Category', def: `Risks are tagged Delivery, Scope, Cost or Other - and the category decides which KPI the risk threatens in the Forecast view: Delivery -> On Time, Scope -> In Full, Cost -> At Cost. Other is informational only. The log is intentionally scoped to OTIFAC-relevant risks - it does not replace full project RAID logs.` },
      { term: 'Forecast view', def: `The Forecast/Actuals toggle on Reports blends completed actuals with in-flight projections, answering "where will we land if in-flight work goes the way it currently looks?". In-flight projects pass each KPI unless there is a concrete miss signal - and a risk only counts once it is material enough (rating at or above the Forecast risk sensitivity set in KPI Tolerances, Amber+ by default):\n• On Time - already past due date + the {{onTimeDays}}-day grace, or a material open Delivery risk\n• In Full - a material open Scope risk\n• At Cost - a material open Cost risk\nA project with no material risks and a future due date passes all three - the Forecast view is only as honest as the risk log.` },
      { term: 'Forecast risk sensitivity', def: `Set in KPI Tolerances (admin). Controls how serious an open risk must be before it dents the Forecast KPIs: Amber and above (recommended), Red only, or any open risk. Green risks (rating 1-3) never move the headline unless sensitivity is set to "any".` },
      { term: 'Risk Register export', def: `Reports → Export → Risk Register downloads every risk (open, mitigated and closed) on the filtered projects in the standard Danone risks & issues register format, ready to paste into the group template.` },
      { term: 'Not assessed', def: `Risks logged before the matrix existed show a "Not assessed" badge. Editing one asks for the full Impact/Likelihood assessment plus a mitigation, upgrading it to the new model.` },
    ],
  },
  {
    title: 'Survey & Responses',
    content: [
      { term: 'Anonymity', def: `Respondent emails are never stored. A one-way hash of the survey token + email is kept solely to prevent duplicate submissions. The email cannot be recovered from the hash.` },
      { term: 'Response threshold', def: `Individual per-stakeholder status is hidden until 5 or more responses are received, to protect anonymity in small teams.` },
      { term: 'Resend', def: `IT can resend the survey at any time. The email only goes to stakeholders who have not yet responded - those who already submitted are skipped. The button shows how many people will receive it.` },
    ],
  },
  {
    title: 'Asana Sync',
    content: [
      { term: 'Field mapping', def: `When importing from Asana, IT maps Asana custom fields to TrackIT fields (Due Date, Go-Live, Forecast Cost, Actual Cost). Mapped fields are read-only in the UI - they update via sync.` },
      { term: 'Daily sync', def: `A scheduled background sync runs every day at 01:00 UTC. It applies field mappings to forecast projects and to completed projects within ~3.5 months of go-live. Cancelled projects, projects without a mapping, and users with an expired Asana token are skipped. Run history is visible to admins in Settings.` },
      { term: 'Manual sync', def: `The "Sync Now" button on a project detail page pulls the latest values from Asana immediately. Only fields with an active mapping are updated; manually-entered fields are not touched.` },
      { term: 'Auth', def: `Asana uses OAuth. The token is stored per-user and refreshed automatically. If the token expires beyond refresh, reconnect via Settings → Asana Integration.` },
    ],
  },
  {
    title: 'Reports & Filters',
    content: [
      { term: 'Scope', def: `The Reports headline tile shows realised + forecast hrs together. KPI tiles below it count only Complete projects; the Pipeline strip counts only Forecast projects. Cancelled projects are excluded from both.` },
      { term: 'Filters', def: `Sub-Portfolio, Tier, and Year filters are cumulative (AND logic). Clearing all filters shows all completed projects.` },
      { term: 'Year filter', def: `Filters by the Actual Go-Live year of the project.` },
      { term: 'KPIs by Tier chart', def: `Shows On Time, In Full, and At Cost percentages broken down by tier, for the current filtered set of projects.` },
      { term: 'Satisfaction Trend', def: `Monthly average satisfaction score across all survey responses (not filtered by project). Shows whether stakeholder sentiment is improving or declining over time.` },
    ],
  },
]

export default function Settings() {
  useDocumentTitle('Settings')
  const api = useApi()
  const asanaApi = useAsanaApi()
  const { user } = useAuth()
  const isAdmin = !!user?.isAdmin
  // When the user can't edit, every input is `disabled` and the Save button
  // is hidden. The backend `requires_admin` gate on PUT /settings is the
  // real enforcement - this is just to keep the UI honest.
  const readOnly = !isAdmin
  const { data, loading } = useData<{ settings: SettingsType; projects: Project[] }>(() =>
    Promise.all([api.getSettings(), api.getProjects()]).then(([settings, projects]) => ({ settings, projects }))
  )
  const [settings, setSettings] = useState<SettingsType | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [asanaUser, setAsanaUser] = useState<{ name: string; email: string; gid: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [savedModal, setSavedModal] = useState(false)
  const [asanaConnecting, setAsanaConnecting] = useState(false)
  const [asanaError, setAsanaError] = useState<string | null>(null)
  const [newSubPort, setNewSubPort] = useState('')
  const [renamingSubPort, setRenamingSubPort] = useState<string | null>(null)
  const [renameSubPortValue, setRenameSubPortValue] = useState('')
  const [newTeam, setNewTeam] = useState('')
  const [renamingTeam, setRenamingTeam] = useState<string | null>(null)
  const [renameTeamValue, setRenameTeamValue] = useState('')
  const [newPortfolioName, setNewPortfolioName] = useState('')
  const [renamingPortfolio, setRenamingPortfolio] = useState<string | null>(null)
  const [renamePortfolioValue, setRenamePortfolioValue] = useState('')
  const [openDoc, setOpenDoc] = useState<string | null>(null)

  useEffect(() => {
    if (data && !settings) {
      setSettings({ ...data.settings, portfolios: data.settings.portfolios ?? [] })
      setProjects(data.projects ?? [])
    }
  }, [data])

  // Restore the Asana "connected" state across navigations. The OAuth token
  // lives server-side; we ask the backend whose token we have, falling back
  // to "not connected" on any error.
  useEffect(() => {
    let cancelled = false
    asanaApi.me().then(u => { if (!cancelled) setAsanaUser(u) }).catch(() => {})
    return () => { cancelled = true }
  }, [asanaApi])


  if (loading || !data || !settings) return <LoadingSpinner />

  const isConnected = !!asanaUser

  const handleConnectAsana = async () => {
    setAsanaError(null)
    setAsanaConnecting(true)
    try {
      const { url } = await asanaApi.connect()
      // Open popup for OAuth
      const popup = window.open(url, 'asana-oauth', 'width=600,height=700,left=200,top=100')
      const onMessage = async (e: MessageEvent) => {
        if (e.origin !== window.location.origin) return
        if (e.data?.type === 'asana-callback' && e.data.code) {
          window.removeEventListener('message', onMessage)
          try {
            const result = await asanaApi.exchangeCode(e.data.code)
            setAsanaUser(result.user)
          } catch {
            setAsanaError('Connection failed.')
          }
          setAsanaConnecting(false)
        }
      }
      window.addEventListener('message', onMessage)
      const timer = setInterval(() => {
        if (popup?.closed) { clearInterval(timer); window.removeEventListener('message', onMessage); setAsanaConnecting(false) }
      }, 500)
    } catch {
      setAsanaError('Failed to start Asana connection.')
      setAsanaConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    setAsanaConnecting(true)
    await asanaApi.disconnect()
    setAsanaUser(null)
    setAsanaConnecting(false)
  }

  const handleSave = async () => {
    setSaving(true)
    await api.updateSettings(settings)
    setSaving(false)
    setSavedModal(true)
  }

  // Sub-portfolio helpers
  const subPortInUse = (name: string) => projects.some(p => p.subPortfolio === name)

  const addSubPort = () => {
    const name = newSubPort.trim()
    if (!name || settings.subPortfolios.includes(name)) return
    setSettings({ ...settings, subPortfolios: [...settings.subPortfolios, name] })
    setNewSubPort('')
  }

  const removeSubPort = (name: string) => {
    if (subPortInUse(name)) return
    setSettings({
      ...settings,
      subPortfolios: settings.subPortfolios.filter(p => p !== name),
      portfolios: (settings.portfolios || []).map(g => ({
        ...g, subPortfolios: g.subPortfolios.filter(s => s !== name),
      })),
    })
  }

  const commitRenameSubPort = (oldName: string) => {
    const newName = renameSubPortValue.trim()
    if (!newName || newName === oldName) { setRenamingSubPort(null); return }
    if (settings.subPortfolios.includes(newName)) { setRenamingSubPort(null); return }
    setSettings({
      ...settings,
      subPortfolios: settings.subPortfolios.map(p => p === oldName ? newName : p),
      portfolios: (settings.portfolios || []).map(g => ({
        ...g, subPortfolios: g.subPortfolios.map(s => s === oldName ? newName : s),
      })),
    })
    setRenamingSubPort(null)
  }

  // Team helpers
  const teamInUse = (name: string) => projects.some(p => p.team === name)

  const addTeam = () => {
    const name = newTeam.trim()
    if (!name || (settings.teams || []).includes(name)) return
    setSettings({ ...settings, teams: [...(settings.teams || []), name] })
    setNewTeam('')
  }

  const removeTeam = (name: string) => {
    if (teamInUse(name)) return
    setSettings({ ...settings, teams: (settings.teams || []).filter(t => t !== name) })
  }

  const commitRenameTeam = (oldName: string) => {
    const newName = renameTeamValue.trim()
    if (!newName || newName === oldName) { setRenamingTeam(null); return }
    setSettings({ ...settings, teams: (settings.teams || []).map(t => t === oldName ? newName : t) })
    setRenamingTeam(null)
  }

  // Portfolio group helpers
  const portfolioInUse = (name: string) =>
    (settings.portfolios || []).find(g => g.name === name)?.subPortfolios.some(s => subPortInUse(s)) ?? false

  const addPortfolioGroup = () => {
    const name = newPortfolioName.trim()
    if (!name || (settings.portfolios || []).some(g => g.name === name)) return
    setSettings({ ...settings, portfolios: [...(settings.portfolios || []), { name, subPortfolios: [] }] })
    setNewPortfolioName('')
  }

  const removePortfolioGroup = (name: string) => {
    if (portfolioInUse(name)) return
    setSettings({ ...settings, portfolios: (settings.portfolios || []).filter(g => g.name !== name) })
  }

  const commitRenamePortfolio = (oldName: string) => {
    const newName = renamePortfolioValue.trim()
    if (!newName || newName === oldName) { setRenamingPortfolio(null); return }
    setSettings({
      ...settings,
      portfolios: (settings.portfolios || []).map(g => g.name === oldName ? { ...g, name: newName } : g),
    })
    setRenamingPortfolio(null)
  }

  // 1-1: assigning a sub-port to a portfolio removes it from any other portfolio first.
  const assignSubPortToPortfolio = (portfolioName: string, subPort: string) => {
    setSettings({
      ...settings,
      portfolios: (settings.portfolios || []).map(g => {
        if (g.name === portfolioName) return { ...g, subPortfolios: [...g.subPortfolios, subPort] }
        return { ...g, subPortfolios: g.subPortfolios.filter(s => s !== subPort) }
      }),
    })
  }

  const unassignSubPort = (portfolioName: string, subPort: string) => {
    setSettings({
      ...settings,
      portfolios: (settings.portfolios || []).map(g =>
        g.name === portfolioName ? { ...g, subPortfolios: g.subPortfolios.filter(s => s !== subPort) } : g
      ),
    })
  }

  const unassignedSubPorts = settings.subPortfolios.filter(
    s => !(settings.portfolios || []).some(g => g.subPortfolios.includes(s))
  )

  return (
    <div className="max-w-2xl">
      <Modal open={savedModal} title="Settings Saved" message="Settings have been saved successfully." onConfirm={() => setSavedModal(false)} />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-slate-500">Configure integrations and defaults</p>
      </div>

      <SettingsTabs active="general" />

      {readOnly && (
        <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500">
          <Lock className="w-3.5 h-3.5 flex-shrink-0" />
          You're viewing settings in read-only mode. Contact an admin to make changes.
        </div>
      )}

      <div className="space-y-6">
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <img src="https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/asana.svg" alt="Asana" className="w-7 h-7" />
            <h2 className="font-bold text-slate-800">Asana Integration</h2>
          </div>

          {isConnected ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-green-800">Connected</div>
                    <div className="text-xs text-green-600">{asanaUser.name}{asanaUser.email && ` · ${asanaUser.email}`}</div>
                  </div>
                </div>
                <button onClick={handleDisconnect} disabled={asanaConnecting} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-white border border-transparent hover:border-slate-200 rounded-lg transition-all disabled:opacity-50">
                  {asanaConnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
                  {asanaConnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
                <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span className="text-xs text-slate-500">A daily background sync runs at 01:00 UTC. Use <span className="font-semibold text-slate-700">Sync Now</span> on a project to pull the latest Asana values immediately.</span>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <button onClick={handleConnectAsana} disabled={asanaConnecting}
                className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors">
                {asanaConnecting ? <><Loader2 className="w-4 h-4 animate-spin" /> Connecting...</> : 'Sign in with Asana'}
              </button>
              {asanaError && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" /> {asanaError}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="card p-6">
          <h2 className="font-bold text-slate-800 mb-1">Sub-Portfolios</h2>
          <p className="text-sm text-slate-500 mb-4">Manage the sub-portfolio options available across the app</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {settings.subPortfolios.map(p => (
              <span key={p} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium">
                {renamingSubPort === p ? (
                  <input
                    autoFocus
                    className="border-b border-slate-400 bg-transparent text-sm w-24 focus:outline-none"
                    value={renameSubPortValue}
                    onChange={e => setRenameSubPortValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') commitRenameSubPort(p); if (e.key === 'Escape') setRenamingSubPort(null) }}
                    onBlur={() => commitRenameSubPort(p)}
                  />
                ) : (
                  <>{p}</>
                )}
                {!readOnly && renamingSubPort !== p && (
                  <>
                    <button type="button" onClick={() => { setRenamingSubPort(p); setRenameSubPortValue(p) }} className="text-slate-400 hover:text-slate-600 transition-colors">
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeSubPort(p)}
                      disabled={subPortInUse(p)}
                      title={subPortInUse(p) ? 'In use by one or more projects' : 'Remove'}
                      className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </span>
            ))}
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              <input type="text" value={newSubPort} onChange={e => setNewSubPort(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addSubPort()}
                className={`flex-1 ${inputClass}`} placeholder="New sub-portfolio name" />
              <button type="button" onClick={addSubPort} disabled={!newSubPort.trim()}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium disabled:opacity-40 transition-colors">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          )}
        </div>

        <div className="card p-6">
          <h2 className="font-bold text-slate-800 mb-1">Portfolios</h2>
          <p className="text-sm text-slate-500 mb-4">Group sub-portfolios under a parent portfolio. Each sub-portfolio belongs to at most one portfolio.</p>
          <div className="space-y-3 mb-4">
            {(settings.portfolios || []).length === 0 && (
              <p className="text-xs text-slate-400">No portfolios yet. Add one below.</p>
            )}
            {(settings.portfolios || []).map(g => (
              <div key={g.name} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2.5">
                  {renamingPortfolio === g.name ? (
                    <input
                      autoFocus
                      className="text-sm font-semibold border-b border-slate-400 bg-transparent focus:outline-none"
                      value={renamePortfolioValue}
                      onChange={e => setRenamePortfolioValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') commitRenamePortfolio(g.name); if (e.key === 'Escape') setRenamingPortfolio(null) }}
                      onBlur={() => commitRenamePortfolio(g.name)}
                    />
                  ) : (
                    <span className="text-sm font-semibold text-slate-800">{g.name}</span>
                  )}
                  {!readOnly && renamingPortfolio !== g.name && (
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => { setRenamingPortfolio(g.name); setRenamePortfolioValue(g.name) }} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removePortfolioGroup(g.name)}
                        disabled={portfolioInUse(g.name)}
                        title={portfolioInUse(g.name) ? 'Contains sub-portfolios in use by projects' : 'Remove'}
                        className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                {/* Assigned sub-portfolios as chips */}
                <div className="flex flex-wrap gap-1.5">
                  {g.subPortfolios.length === 0 && (
                    <span className="text-xs text-slate-400 italic">No sub-portfolios assigned</span>
                  )}
                  {g.subPortfolios.map(s => (
                    <span key={s} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 bg-danone-cyan/10 text-danone-cyan border border-danone-cyan/20 rounded-md text-xs font-medium">
                      {s}
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => unassignSubPort(g.name, s)}
                          disabled={subPortInUse(s)}
                          title={subPortInUse(s) ? 'In use by projects - reassign projects first' : 'Remove from portfolio'}
                          className="text-danone-cyan/60 hover:text-danone-cyan transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                {/* Add unassigned sub-port dropdown */}
                {!readOnly && unassignedSubPorts.length > 0 && (
                  <div className="mt-2">
                    <select
                      className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 bg-white focus:outline-none focus:border-danone-cyan cursor-pointer"
                      value=""
                      onChange={e => { if (e.target.value) assignSubPortToPortfolio(g.name, e.target.value) }}
                    >
                      <option value="">+ Add sub-portfolio</option>
                      {unassignedSubPorts.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
                {!readOnly && unassignedSubPorts.length === 0 && g.subPortfolios.length < settings.subPortfolios.length && (
                  <p className="mt-2 text-xs text-slate-400">All sub-portfolios are assigned to a portfolio.</p>
                )}
              </div>
            ))}
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              <input type="text" value={newPortfolioName} onChange={e => setNewPortfolioName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addPortfolioGroup()}
                className={`flex-1 ${inputClass}`} placeholder="New portfolio name (e.g. Finance)" />
              <button type="button" onClick={addPortfolioGroup} disabled={!newPortfolioName.trim()}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium disabled:opacity-40 transition-colors">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          )}
        </div>

        <div className="card p-6">
          <h2 className="font-bold text-slate-800 mb-1">Teams</h2>
          <p className="text-sm text-slate-500 mb-4">Tags used by the Team filter on Reports. Set the team on each project individually.</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {(settings.teams || []).map(t => (
              <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium">
                {renamingTeam === t ? (
                  <input
                    autoFocus
                    className="border-b border-slate-400 bg-transparent text-sm w-24 focus:outline-none"
                    value={renameTeamValue}
                    onChange={e => setRenameTeamValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') commitRenameTeam(t); if (e.key === 'Escape') setRenamingTeam(null) }}
                    onBlur={() => commitRenameTeam(t)}
                  />
                ) : (
                  <>{t}</>
                )}
                {!readOnly && renamingTeam !== t && (
                  <>
                    <button type="button" onClick={() => { setRenamingTeam(t); setRenameTeamValue(t) }} className="text-slate-400 hover:text-slate-600 transition-colors">
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeTeam(t)}
                      disabled={teamInUse(t)}
                      title={teamInUse(t) ? 'In use by one or more projects' : 'Remove'}
                      className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </span>
            ))}
            {(settings.teams || []).length === 0 && (
              <p className="text-xs text-slate-400">No teams yet</p>
            )}
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              <input type="text" value={newTeam} onChange={e => setNewTeam(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTeam()}
                className={`flex-1 ${inputClass}`} placeholder="New team name" />
              <button type="button" onClick={addTeam} disabled={!newTeam.trim()}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium disabled:opacity-40 transition-colors">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          )}
        </div>

        {!readOnly && (
          <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {saving ? 'Saving...' : 'Save Settings'}
          </button>
        )}

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-5 h-5 text-slate-500" />
            <h2 className="font-bold text-slate-800">How it works</h2>
          </div>
          <div className="space-y-2">
            {DOCS.map(section => (
              <div key={section.title} className="border border-slate-200 rounded-xl overflow-hidden">
                <button type="button" onClick={() => setOpenDoc(openDoc === section.title ? null : section.title)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                  {section.title}
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${openDoc === section.title ? 'rotate-180' : ''}`} />
                </button>
                {openDoc === section.title && (
                  <div className="px-4 pb-4 space-y-3 border-t border-slate-100">
                    {section.content.map(item => {
                      const tokenise = (text: string) => {
                        const onTime = String(settings?.onTimeDays ?? 14)
                        const atCost = String(settings?.atCostPct ?? 10)
                        return text
                          .replace(/\{\{onTimeDays\}\}/g, `\x00${onTime}\x00`)
                          .replace(/\{\{atCostPct\}\}/g, `\x00${atCost}\x00`)
                          .split('\x00')
                      }
                      const chip = (t: string, i: number) => i % 2 === 1
                        ? <span key={i} className="px-1 py-0.5 mx-0.5 bg-blue-100 text-blue-700 rounded text-[11px] font-mono font-semibold">{t}</span>
                        : <span key={i}>{t}</span>
                      const renderInline = (text: string) => <>{tokenise(text).map(chip)}</>
                      const renderFormula = (line: string) => (
                        <div className="mt-1 px-2 py-1 bg-slate-100 rounded text-[11px] font-mono text-slate-600 flex flex-wrap items-center gap-0">
                          {tokenise(line).map(chip)}
                        </div>
                      )
                      return (
                        <div key={item.term} className="pt-3">
                          <div className="text-xs font-bold text-slate-700 mb-0.5">{item.term}</div>
                          {item.def.split('\n').map((line, i) => {
                            if (line.startsWith('Formula:')) return (
                              <div key={i} className="mt-2 flex gap-3 items-start">
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 w-14 shrink-0 pt-1">Formula</span>
                                {renderFormula(line.replace('Formula: ', ''))}
                              </div>
                            )
                            if (line.startsWith('Example:')) return (
                              <div key={i} className="mt-1 flex gap-3 items-start">
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 w-14 shrink-0 pt-0.5">Example</span>
                                <div className="text-[11px] text-slate-500 italic leading-relaxed">{line.replace('Example: ', '')}</div>
                              </div>
                            )
                            if (line.startsWith('Rule:')) return (
                              <div key={i} className="mt-1 flex gap-3 items-start">
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 w-14 shrink-0 pt-0.5">Rule</span>
                                <div className="text-[11px] text-slate-500 leading-relaxed">{renderInline(line.replace('Rule: ', ''))}</div>
                              </div>
                            )
                            return <div key={i} className="text-xs text-slate-500 leading-relaxed">{renderInline(line)}</div>
                          })}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
